import { useRef, useEffect, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { useTenant } from '../../contexts/TenantContext'
import { NotificationManager } from '../features/NotificationManager'
import { AIChatWidget } from '../features/AIChatWidget'
import { TournamentAssistant } from '../features/TournamentAssistant'
import { TournamentSetupProgress } from '../admin/tournament/TournamentSetupProgress'
import { supabase } from '../../lib/supabase'
import { isDemoTournament } from '../../data/demoData'

export const Layout = ({ children }) => {
  const { activeTenant } = useTenant()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const containerRef = useRef(null)

  const isDashboard = location.pathname === '/admin/dashboard' || location.pathname === '/'
  const isTournamentAdmin = location.pathname.startsWith('/admin/tournaments/')

  const [currentBgIndex, setCurrentBgIndex] = useState(0)
  const [sportType, setSportType] = useState('goals')

  // Extract tournament ID from route path
  const match = location.pathname.match(/\/admin\/tournaments\/([^/]+)/)
  const tournamentId = match ? match[1] : null

  useEffect(() => {
    const fetchSportType = async () => {
      console.log('[Layout] fetchSportType matching path:', location.pathname, 'extracted tournamentId:', tournamentId)
      if (!tournamentId) {
        setSportType('goals')
        return
      }

      try {
        if (isDemoTournament(tournamentId)) {
          const type = tournamentId === '87654321-4321-4321-4321-210987654321' ? 'esports' : 'goals'
          console.log('[Layout] demo tournament matched, setting sportType:', type)
          setSportType(type)
          return
        }

        const { data, error } = await supabase
          .from('tournaments')
          .select('sport_type')
          .eq('id', tournamentId)
          .single()
        
        if (error) {
          console.error('[Layout] supabase query error:', error)
          throw error
        }

        if (data) {
          console.log('[Layout] supabase query successful, sport_type is:', data.sport_type)
          setSportType(data.sport_type || 'goals')
        } else {
          console.log('[Layout] no data returned for tournamentId:', tournamentId)
        }
      } catch (err) {
        console.error('[Layout] Error loading sport type in Layout:', err)
      }
    }

    fetchSportType()
  }, [tournamentId, location.pathname])

  useEffect(() => {
    if (containerRef.current && activeTenant) {
      const root = containerRef.current
      root.style.setProperty('--primary', activeTenant.primary_color || '#ea580c')
      root.style.setProperty('--secondary', activeTenant.secondary_color || '#22c55e')
    }
  }, [activeTenant])

  // Shuffle Logic: Change background ONLY when visiting Dashboard
  useEffect(() => {
    // Only run if enabled and there are multiple images
    if (!activeTenant?.slideshow_enabled || !activeTenant?.background_images?.length || activeTenant.background_images.length <= 1) return

    // Verify if we just entered the dashboard
    if (isDashboard) {
      // Pick a random index distinct from the current one (if possible)
      setCurrentBgIndex(prev => {
        let next
        do {
          next = Math.floor(Math.random() * activeTenant.background_images.length)
        } while (next === prev && activeTenant.background_images.length > 1)

        console.log('Dashboard bezocht: Nieuwe achtergrond #', next)
        return next
      })
    }
  }, [isDashboard, activeTenant]) // Trigger when route changes (isDashboard) or tenant settings load

  const filterParam = searchParams.get('filter')
  const isEsports = sportType === 'esports' || (isDashboard && filterParam === 'esports')
  const isSets = sportType === 'sets' || (isDashboard && filterParam === 'sets')

  const getBackgroundStyle = () => {
    if (isEsports) {
      return {
        backgroundImage: `url("/images/E-sports.webp")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#030306'
      }
    }

    if (isSets) {
      return {
        backgroundImage: `url("/images/padel.webp")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#050a06'
      }
    }

    let bg = activeTenant?.background_image_url

    // Override if slideshow is active
    if (activeTenant?.slideshow_enabled && activeTenant?.background_images?.length > 0) {
      bg = activeTenant.background_images[currentBgIndex]
    }

    if (!bg) return {}

    if (bg.startsWith('http') || bg.startsWith('/')) {
      return {
        backgroundImage: `url(${bg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }
    }

    return { background: bg }
  }

  // Dashboard has transparent container (widgets float), others have glass sheet
  const containerClasses = isDashboard
    ? isEsports
      ? "flex-1 overflow-auto bg-[#07050e]/85 backdrop-blur-xl m-4 rounded-3xl border border-purple-500/25 shadow-[0_0_40px_rgba(168,85,247,0.15)] relative text-slate-100 layout-esports p-6"
      : isSets
        ? "flex-1 overflow-auto bg-[#040806]/85 backdrop-blur-xl m-4 rounded-3xl border border-lime-500/25 shadow-[0_0_40px_rgba(163,230,53,0.15)] relative text-slate-100 layout-racketsports p-6"
        : "flex-1 overflow-auto m-4 relative" // Transparent, no border/shadow
    : isEsports
      ? "flex-1 overflow-auto bg-[#07050e]/85 backdrop-blur-xl m-4 rounded-3xl border border-purple-500/25 shadow-[0_0_40px_rgba(168,85,247,0.15)] relative text-slate-100 layout-esports"
      : isSets
        ? "flex-1 overflow-auto bg-[#040806]/85 backdrop-blur-xl m-4 rounded-3xl border border-lime-500/25 shadow-[0_0_40px_rgba(163,230,53,0.15)] relative text-slate-100 layout-racketsports"
        : "flex-1 overflow-auto bg-white/40 backdrop-blur-md m-4 rounded-3xl border border-white/20 shadow-2xl relative"

  return (
    <div ref={containerRef} className="flex h-[100dvh] overflow-hidden animate-fade-in print:block print:h-auto print:overflow-visible print:bg-white print:!bg-none" style={getBackgroundStyle()}>
      <div className="print:hidden h-full flex shrink-0">
        <Sidebar />
      </div>
      <div className={`${containerClasses} print:m-0 print:border-none print:shadow-none print:bg-white print:!bg-none print:overflow-visible print:block print:p-0`}>
        <div className="print:hidden">
          <NotificationManager />
          {isTournamentAdmin && <TournamentSetupProgress />}
        </div>
        {children}
      </div>
      <div className="print:hidden">
        <TournamentAssistant />
        <AIChatWidget />
      </div>
    </div>
  )
}
